import { LeaDataModel } from "@/models";

type RecordType =
  | "CHLD"
  | "ENRL"
  | "CLEN"
  | "FAMI"
  | "LANG"
  | "SLNG"
  | "STFF"
  | "SASS"
  | "SCLR";

export function getFreshRecordData(
  leaData: LeaDataModel | undefined,
  recordType: RecordType | string,
  referenceRecord: Record<string, unknown>,
  entityId: string | undefined
): Record<string, unknown> {
  if (!leaData) {
    return referenceRecord;
  }

  const ref = referenceRecord as any;
  const csppid = ref?.CSPPID ?? entityId;
  const localStaffId = ref?.LocalStaffId ?? entityId;

  switch (recordType) {
    case "CHLD": {
      const chldRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
      return (
        (chldRecord as unknown as Record<string, unknown>) ?? referenceRecord
      );
    }

    case "LANG": {
      const chldRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
      const langRecords = (chldRecord as any)?.LangRecords ?? [];

      let matchingRecord = langRecords.find(
        (r: any) =>
          r.EffectiveStartDate === ref.EffectiveStartDate &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = langRecords.find(
          (r: any) =>
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && langRecords.length === 1) {
        matchingRecord = langRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    case "SLNG": {
      const chldRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
      const slngRecords = (chldRecord as any)?.SLngRecords ?? [];

      let matchingRecord = slngRecords.find(
        (r: any) =>
          r.EffectiveStartDate === ref.EffectiveStartDate &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = slngRecords.find(
          (r: any) =>
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && slngRecords.length === 1) {
        matchingRecord = slngRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    case "FAMI": {
      const chldRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
      const famiRecords = (chldRecord as any)?.FamilyRecords ?? [];

      let matchingRecord = famiRecords.find(
        (r: any) =>
          r.LocalFamilyId === ref.LocalFamilyId &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = famiRecords.find(
          (r: any) =>
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && famiRecords.length === 1) {
        matchingRecord = famiRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    case "ENRL": {
      const chldRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
      const enrlRecords = (chldRecord as any)?.Enrollments ?? [];

      let matchingRecord = enrlRecords.find(
        (r: any) =>
          r.EffectiveStartDate === ref.EffectiveStartDate &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = enrlRecords.find(
          (r: any) =>
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && enrlRecords.length === 1) {
        matchingRecord = enrlRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    case "CLEN": {
      const chldRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
      const clenRecords = (chldRecord as any)?.ClassroomAssignments ?? [];

      // Try exact match first
      let matchingRecord = clenRecords.find(
        (r: any) =>
          r.LocalClassroomId === ref.LocalClassroomId &&
          r.AssignmentStartDate === ref.AssignmentStartDate &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = clenRecords.find(
          (r: any) =>
            r.LocalClassroomId === ref.LocalClassroomId &&
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && clenRecords.length === 1) {
        matchingRecord = clenRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    case "STFF": {
      const stffRecord = leaData.stff?.find(
        (s: any) => String(s.LocalStaffId) === String(localStaffId)
      );
      return (
        (stffRecord as unknown as Record<string, unknown>) ?? referenceRecord
      );
    }

    case "SASS": {
      const stffRecord = leaData.stff?.find(
        (s: any) => String(s.LocalStaffId) === String(localStaffId)
      );
      const sassRecords = (stffRecord as any)?.StaffAssignments ?? [];

      let matchingRecord = sassRecords.find(
        (r: any) =>
          r.AssignmentStartDate === ref.AssignmentStartDate &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = sassRecords.find(
          (r: any) =>
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && sassRecords.length === 1) {
        matchingRecord = sassRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    case "SCLR": {
      const stffRecord = leaData.stff?.find(
        (s: any) => String(s.LocalStaffId) === String(localStaffId)
      );
      const sclrRecords = (stffRecord as any)?.ClassroomAssignments ?? [];

      let matchingRecord = sclrRecords.find(
        (r: any) =>
          r.LocalClassroomId === ref.LocalClassroomId &&
          r.AssignmentStartDate === ref.AssignmentStartDate &&
          r.ProgramYear === ref.ProgramYear &&
          r.PreschoolCode === ref.PreschoolCode
      );

      if (!matchingRecord) {
        matchingRecord = sclrRecords.find(
          (r: any) =>
            r.LocalClassroomId === ref.LocalClassroomId &&
            r.ProgramYear === ref.ProgramYear &&
            r.PreschoolCode === ref.PreschoolCode
        );
      }

      if (!matchingRecord && sclrRecords.length === 1) {
        matchingRecord = sclrRecords[0];
      }

      return matchingRecord ?? referenceRecord;
    }

    default:
      return referenceRecord;
  }
}
